import { FormControl, MenuItem, Select as MuiSelect } from '@mui/material';
import { useTranslate } from '@si/utils';
import { useId, useState } from 'react';

import { FormHelperText } from '../FormHelperText/FormHelperText';
import { FormLabel } from '../FormLabel/FormLabel';
import { useColors } from '../ThemeProvider';

interface Props<T extends string | number> {
  id?: string;
  label: string;
  hideLabel?: boolean;
  options: { label: string; value: T }[];
  value: T | undefined;
  onChange: (value: T | undefined) => void;
  onOpen?: () => void;
  onMenuItemClick?: (value: T) => void;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  overrideVisited?: boolean;
  inputWidth?: '10rem' | '15rem' | '20rem' | '25rem' | '100%';
}

export function Select<T extends string | number>({
  id,
  label,
  hideLabel = false,
  onChange,
  onOpen,
  onMenuItemClick,
  options,
  value,
  required = false,
  disabled = false,
  helperText = '',
  overrideVisited = false,
  inputWidth = '100%',
}: Props<T>) {
  const { t, translate } = useTranslate();
  const { errorDark, textPrimary } = useColors();
  const generatedId = useId();

  const [visited, setVisited] = useState(false);

  const selectId = id ?? generatedId;
  const labelId = `${selectId}-label`;
  const helperTextId = `${selectId}-helper-text`;
  const error = required && !value;
  const showError = error && (visited || overrideVisited);

  const maxWidth = inputWidth === '10rem' || inputWidth === '15rem' ? '20rem' : inputWidth;
  const fieldColor = showError ? errorDark : textPrimary;

  return (
    <FormControl sx={{ width: inputWidth, maxWidth }} disabled={disabled}>
      {!hideLabel && (
        <FormLabel
          componentId={selectId}
          id={labelId}
          label={label}
          required={required}
          valid={!error}
        />
      )}

      <MuiSelect
        id={selectId}
        name={selectId}
        labelId={hideLabel ? undefined : labelId}
        inputProps={hideLabel ? { 'aria-label': label } : undefined}
        value={value ?? ''}
        onChange={({ target: { value } }) => onChange(value as T)}
        onOpen={onOpen}
        onClose={() => setVisited(true)}
        error={showError}
        disabled={disabled}
        sx={{
          width: inputWidth,
          maxWidth: '100%',
          color: fieldColor,
          '& .MuiInputBase-input': {
            padding: '8px 12px',
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            onClick={() => onMenuItemClick?.(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
      </MuiSelect>

      <FormHelperText
        id={helperTextId}
        showError={showError}
        text={showError ? translate(t.fieldRequired, { field: label }) : helperText}
      />
    </FormControl>
  );
}
